import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  WalletCards,
  TrendingUp,
  BadgeDollarSign,
  Dumbbell,
  ShoppingBag,
  ShoppingCart,
  HandCoins,
  CheckCircle2,
  ClipboardList,
  ScanLine,
  Snowflake,
  MessageSquare,
  Palette,
  Landmark,
  Store,
  FileSpreadsheet,
  UserCog,
  Ban,
  LogOut,
  BarChart3,
  BookOpen,
  Calculator
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { BrandLogo } from './BrandLogo';

const navItems = [
  { name: 'Dashboard', path: '/', icon: LayoutDashboard },
  { name: 'Executive Dashboard', path: '/executive-dashboard', icon: BarChart3 },
  { name: 'Accounting', path: '/accounting', icon: BookOpen },
  { name: 'Master Accrue', path: '/master-accrue', icon: Calculator },
  { name: 'Scan', path: '/scan', icon: ScanLine },
  { name: 'Point of Sale', path: '/pos', icon: ShoppingCart },
  { name: 'Cancel Transaksi', path: '/transaction-cancel', icon: Ban },
  { name: 'Petty Cash', path: '/petty-cash', icon: HandCoins },
  { name: 'Approve Reimburse', path: '/approve-reimburse', icon: CheckCircle2 },
  { name: 'Payment Request', path: '/product-payment-requests', icon: WalletCards },
  { name: 'Member List', path: '/members', icon: Users },
  { name: 'Paket BF', path: '/payments', icon: CreditCard },
  { name: 'Expenses', path: '/expenses', icon: WalletCards },
  { name: 'Profit Loss', path: '/profit-loss', icon: TrendingUp },
  { name: 'Laporan Harian', path: '/daily-report', icon: FileSpreadsheet },
  { name: 'Laporan Bulanan Kasir', path: '/cashier-monthly-report', icon: FileSpreadsheet },
  { name: 'Laporan Bulanan', path: '/monthly-report', icon: FileSpreadsheet },
  { name: 'Laporan Titipan', path: '/consignment-report', icon: Store },
  { name: 'Plans', path: '/plans', icon: BadgeDollarSign },
  { name: 'Master PT', path: '/pt-menu', icon: Dumbbell },
  { name: 'Rate Sesi PT', path: '/pt-session-rates', icon: Dumbbell },
  { name: 'Master Coach', path: '/cardio-coaches', icon: Users },
  { name: 'Master Product', path: '/master-products', icon: ShoppingBag },
  { name: 'Master Titipan', path: '/consignment-products', icon: Store },
  { name: 'Merchant', path: '/merchants', icon: Store },
  { name: 'Master Bank', path: '/master-banks', icon: Landmark },
  { name: 'Master Rekening', path: '/master-rekening', icon: WalletCards },
  { name: 'Freeze Approval', path: '/freeze-approval', icon: Snowflake },
  { name: 'Users', path: '/users', icon: UserCog },
  { name: 'Order', path: '/orders', icon: ClipboardList },
  { name: 'Contact Us', path: '/contact-us', icon: MessageSquare },
  { name: 'Branding', path: '/branding', icon: Palette },
];

export function Sidebar() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const role = String(user?.role || '').trim().toLowerCase();
  const adminAllowedPaths = new Set([
    '/',
    '/executive-dashboard',
    '/scan',
    '/pt-menu',
    '/cardio-coaches',
    '/master-products',
    '/approve-reimburse',
    '/product-payment-requests',
    '/transaction-cancel',
    '/expenses',
    '/profit-loss',
    '/daily-report',
    '/monthly-report',
    '/consignment-report',
    '/plans',
    '/merchants',
    '/pt-session-rates',
    '/master-banks',
    '/master-rekening',
    '/freeze-approval',
    '/users',
    '/contact-us',
    '/branding',
    '/accounting',
    '/master-accrue',
  ]);
  const kasirAllowedPaths = new Set([
    '/payments',
    '/pos',
    '/transaction-cancel',
    '/members',
    '/pt-menu',
    '/cardio-coaches',
    '/master-products',
    '/consignment-products',
    '/petty-cash',
    '/product-payment-requests',
    '/orders',
    '/daily-report',
    '/cashier-monthly-report',
    '/scan',
  ]);
  const kasirMenuOrder = [
    '/scan',
    '/payments',
    '/pos',
    '/transaction-cancel',
    '/members',
    '/pt-menu',
    '/cardio-coaches',
    '/master-products',
    '/consignment-products',
    '/petty-cash',
    '/product-payment-requests',
    '/orders',
    '/daily-report',
    '/cashier-monthly-report',
  ];
  const isAdmin = role === 'admin';
  const visibleNavItems = isAdmin
    ? navItems.filter((item) => adminAllowedPaths.has(item.path))
    : navItems
        .filter((item) => kasirAllowedPaths.has(item.path))
        .sort((a, b) => kasirMenuOrder.indexOf(a.path) - kasirMenuOrder.indexOf(b.path));

  const handleSmoothNavigate = (event, path) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey
    ) {
      return;
    }
    event.preventDefault();
    if (document.startViewTransition) {
      document.startViewTransition(() => navigate(path));
      return;
    }
    navigate(path);
  };

  return (
    <aside className="fixed h-screen w-64 border-r border-[#D4AF37]/15 bg-[radial-gradient(circle_at_0%_0%,rgba(212,175,55,0.09)_0%,rgba(9,9,9,0)_22%),linear-gradient(180deg,#070708_0%,#0b0b0d_100%)] px-5 py-6">
      <div className="flex h-full flex-col">
        {/* Logo */}
        <div className="mb-6 border-b border-white/10 pb-5 pl-1">
          <BrandLogo size="sm" showText textClassName="leading-tight" />
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1.5 overflow-y-auto pr-1">
          {visibleNavItems.map((item) => {
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                onClick={(event) => handleSmoothNavigate(event, item.path)}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `group relative flex min-h-[46px] items-center gap-3 rounded-sm border px-3.5 py-2.5 transition-all duration-200 ${
                    isActive
                      ? 'border-[#D4AF37]/35 bg-[linear-gradient(90deg,rgba(212,175,55,0.16)_0%,rgba(212,175,55,0.03)_72%)] text-[#D4AF37] shadow-[0_0_0_1px_rgba(212,175,55,0.08),inset_0_1px_0_rgba(255,255,255,0.04)]'
                      : 'border-transparent text-zinc-400 hover:border-white/10 hover:bg-zinc-900/60 hover:text-zinc-100'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="grid h-5 w-5 shrink-0 place-items-center">
                      <item.icon
                        className={`h-[18px] w-[18px] transition-colors ${
                          isActive ? 'text-[#D4AF37]' : 'text-zinc-500 group-hover:text-zinc-300'
                        }`}
                        strokeWidth={1.7}
                      />
                    </span>
                    <span className="truncate text-sm font-medium leading-none tracking-[-0.01em]">
                      {item.name}
                    </span>
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Logout Button */}
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="mt-4 flex min-h-[46px] items-center gap-3 rounded-sm border border-transparent px-3.5 py-2.5 text-zinc-400 transition-all duration-200 hover:border-red-500/30 hover:bg-red-500/10 hover:text-red-400"
        >
          <span className="grid h-5 w-5 shrink-0 place-items-center">
            <LogOut className="h-[18px] w-[18px]" strokeWidth={1.7} />
          </span>
          <span className="text-sm font-medium leading-none tracking-[-0.01em]">Logout</span>
        </button>
      </div>
    </aside>
  );
}
